import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Mail, MessageSquare, Lock, Phone, Star, Send, LifeBuoy, Check
} from "lucide-react";
import { createSupportTicket } from "../api/client";

// Helper — check if user's plan includes a specific support channel
function hasSupportChannel(user, channel) {
  if (!user || user.role === "admin") return true; // Admin always has full access
  const support = (user.support || user.service_plan_support || "chatbot").toLowerCase();
  return support.includes(channel.toLowerCase());
}

// Locked support item with upgrade prompt showing options transparently in the background
function LockedSupportItem({ label, icon, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        position: "relative", borderRadius: "var(--radius)", overflow: "hidden",
        border: "1px solid rgba(239,68,68,0.15)", background: "rgba(239,68,68,0.03)",
        transition: "all 0.2s ease"
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Semi-transparent background content to keep option details visible */}
      <div style={{
        display: "flex", alignItems: "center", gap: "12px",
        padding: "16px 20px", opacity: 0.35, userSelect: "none",
        pointerEvents: "none"
      }}>
        {icon}
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: "13px", fontWeight: "700" }}>{label}</div>
          <div style={{ fontSize: "11px", color: "var(--text2)" }}>Locked support channel</div>
        </div>
      </div>
      {/* Interactive Overlay displaying what option is being unlocked */}
      <div
        style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", gap: "6px", flexDirection: "column",
          background: hovered ? "rgba(239,68,68,0.09)" : "rgba(10,12,18,0.72)",
          backdropFilter: "blur(1px)", cursor: "pointer",
          transition: "background 0.2s ease"
        }}
        onClick={() => navigate("/renew-plan")}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Lock size={12} style={{ color: "var(--red)" }} />
          <span style={{ fontSize: "12px", fontWeight: "800", color: "var(--red)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Locked</span>
        </div>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--text)" }}>
          Unlock {label}
        </span>
      </div>
    </div>
  );
}

export default function Support() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState({ subject: "", category: "webhook", message: "" });
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!ticket.subject.trim() || !ticket.message.trim()) {
      showToast("Please fill out all fields before submitting", "error");
      return;
    }
    setSubmitting(true);
    try {
      const data = await createSupportTicket({
        subject: ticket.subject.trim(),
        category: ticket.category.trim(),
        message: ticket.message.trim()
      });
      showToast(data.message || "Support ticket created successfully!");
      setTicket({ subject: "", category: "webhook", message: "" });
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to submit support ticket", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const canEmail    = hasSupportChannel(user, "email");
  const canTelegram = hasSupportChannel(user, "telegram");
  const canPhone    = hasSupportChannel(user, "contact number");

  return (
    <div style={{ paddingBottom: "24px" }}>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      <div className="page-header">
        <div>
          <div className="page-title">Support Hub</div>
          <div className="page-sub">Access your support channels and submit custom technical help tickets</div>
        </div>
      </div>

      <div className="grid-2">
        {/* ── LEFT COLUMN: SUPPORT TIERS ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div className="card" style={{ padding: 24, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
              <LifeBuoy size={20} style={{ color: "var(--accent)" }} />
              <div style={{ fontWeight: 700, fontSize: 16 }}>Your Active Support Channels</div>
            </div>

            {user?.role !== "admin" && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                fontSize: "11px", fontWeight: "700", color: "var(--accent)",
                background: "rgba(0,242,254,0.06)", border: "1px solid rgba(0,242,254,0.2)",
                borderRadius: "20px", padding: "3px 10px", marginBottom: "16px"
              }}>
                <Star size={10} /> {user?.service_plan?.toUpperCase() || "DEMO"} Plan active permissions
              </div>
            )}

            <p style={{ color: "var(--text2)", fontSize: "13px", marginBottom: "20px", lineHeight: "1.5" }}>
              Unlock direct communication lanes with our developers. Higher tiers grant telegram chat boxes and personal telephone lines.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Chatbot — always free */}
              <button
                className="btn btn-ghost"
                style={{ justifyContent: "flex-start", width: "100%", padding: "12px 16px", cursor: "pointer" }}
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("open-pinebot"));
                }}
              >
                <MessageSquare size={16} />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: "13px", fontWeight: "700" }}>PineBot Assistant</div>
                  <div style={{ fontSize: "11px", color: "var(--text2)" }}>Ask PineBot any question contextually</div>
                </div>
                <span style={{ marginLeft: "auto", fontSize: "10px", background: "rgba(16,185,129,0.1)", color: "var(--green)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: "10px", padding: "2px 6px", fontWeight: "700" }}>
                  FREE
                </span>
              </button>

              {/* Email Support — gated */}
              {canEmail ? (
                <a
                  href="mailto:support@tradestreet.in"
                  className="btn btn-ghost"
                  style={{ justifyContent: "flex-start", width: "100%", padding: "12px 16px", textDecoration: "none" }}
                >
                  <Mail size={16} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "13px", fontWeight: "700" }}>Direct Email Support</div>
                    <div style={{ fontSize: "11px", color: "var(--text2)" }}>support@tradestreet.in</div>
                  </div>
                </a>
              ) : (
                <LockedSupportItem label="Email Support" icon={<Mail size={16} />} navigate={navigate} />
              )}

              {/* Telegram — gated */}
              {canTelegram ? (
                <a
                  href="https://t.me/TradeStreetSupport"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-ghost"
                  style={{ justifyContent: "flex-start", width: "100%", padding: "12px 16px", textDecoration: "none" }}
                >
                  <MessageSquare size={16} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "13px", fontWeight: "700" }}>Telegram Support Chat</div>
                    <div style={{ fontSize: "11px", color: "var(--text2)" }}>@TradeStreetSupport</div>
                  </div>
                </a>
              ) : (
                <LockedSupportItem label="Telegram Support" icon={<MessageSquare size={16} />} navigate={navigate} />
              )}

              {/* Phone Line — gated */}
              {canPhone ? (
                <div
                  className="btn btn-ghost"
                  style={{ justifyContent: "flex-start", width: "100%", padding: "12px 16px", cursor: "default" }}
                >
                  <Phone size={16} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "13px", fontWeight: "700" }}>Personal Help Number</div>
                    <div style={{ fontSize: "11px", color: "var(--text2)" }}>+91 98765 43210</div>
                  </div>
                  <span style={{ marginLeft: "auto", fontSize: "10px", background: "rgba(139,92,246,0.1)", color: "var(--accent-purple)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: "10px", padding: "2px 6px", fontWeight: "700" }}>
                    PREMIUM
                  </span>
                </div>
              ) : (
                <LockedSupportItem label="Personal Contact Number" icon={<Phone size={16} />} navigate={navigate} />
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN: CREATE TICKET FORM ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <form className="card" style={{ padding: 24, marginBottom: 0 }} onSubmit={handleCreateTicket}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
              <Send size={20} style={{ color: "var(--accent)" }} />
              <div style={{ fontWeight: 700, fontSize: 16 }}>Submit Support Ticket</div>
            </div>

            <div className="form-group">
              <label className="form-label">Subject</label>
              <input
                className="form-input"
                type="text"
                placeholder="Brief summary of your technical issue"
                value={ticket.subject}
                onChange={e => setTicket(p => ({ ...p, subject: e.target.value }))}
                disabled={submitting}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Inquiry Category</label>
              <select
                className="form-input"
                value={ticket.category}
                onChange={e => setTicket(p => ({ ...p, category: e.target.value }))}
                disabled={submitting}
                style={{ background: "var(--bg-dark)", color: "var(--text)", border: "1px solid var(--border)", outline: "none", cursor: "pointer" }}
              >
                <option value="webhook">Webhook Signal Delivery Issue</option>
                <option value="ea_connection">MT5 EA Connection / DLL configuration</option>
                <option value="billing">Subscription / Razorpay Payments</option>
                <option value="other">Other Technical Inquiry</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Problem Description</label>
              <textarea
                className="form-input"
                placeholder="Describe your issue with as many details as possible (magic number, broker name, exact error logs, etc.)"
                value={ticket.message}
                onChange={e => setTicket(p => ({ ...p, message: e.target.value }))}
                disabled={submitting}
                style={{ minHeight: "130px", resize: "vertical", lineHeight: "1.5" }}
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center", gap: "8px" }}
              disabled={submitting}
            >
              <Send size={16} />
              {submitting ? "Creating Ticket..." : "Submit Support Ticket"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
